import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const VAPID_PUBLIC_KEY = 'BMEyCF9vZiC-mKLjFNfVYhKAioei9KPnNJM_AhTHHoxINq-o703k3uCmpeW0-quyOs7NWCwgsXgAnMFpuS1kS6Q';

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray as Uint8Array;
}

/**
 * Request notification permission and subscribe to push notifications
 * Saves subscription to Firestore at notificationEndpoints/{userId}
 */
export async function subscribeToPushNotifications(
  userId: string,
  userEmail: string,
  companyId: string
): Promise<{ success: boolean; error?: string }> {
  console.log('🔔 [Mobile Push] Starting push subscription process');
  
  try {
    // Check if Firestore DB is available
    if (!db) {
      console.error('🔔 [Mobile Push] Firestore DB not initialized');
      return { success: false, error: 'Database not available' };
    }
    
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.error('🔔 [Mobile Push] Notifications API not supported');
      return { success: false, error: 'Notifications not supported' };
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      console.error('🔔 [Mobile Push] Service Worker not supported');
      return { success: false, error: 'Service workers not supported' };
    }
    
    // Request notification permission
    const permission = await Notification.requestPermission();
    console.log('🔔 [Mobile Push] Permission after request:', permission);
    
    if (permission !== 'granted') {
      console.warn('🔔 [Mobile Push] Permission denied or dismissed');
      return { success: false, error: 'Notification permission denied' };
    }

    // Wait for ready with timeout
    const waitForReady = async (ms: number) => {
      const registrationPromise = navigator.serviceWorker.ready;
      const timeoutPromise = new Promise((_res, reject) =>
        setTimeout(() => reject(new Error(`Service worker ready timeout after ${ms}ms`)), ms)
      );
      return Promise.race([registrationPromise, timeoutPromise]) as Promise<ServiceWorkerRegistration>;
    };

    let registration: ServiceWorkerRegistration | null = null;
    try {
      registration = await waitForReady(30000);
      console.log('🔔 [Mobile Push] Service worker ready');
    } catch (e) {
      console.log('🔔 [Mobile Push] Service worker not ready, attempting to register /sw.js');
      try {
        // Try to register as module first
        try {
          await navigator.serviceWorker.register('/sw.js', { type: 'module' });
          registration = await waitForReady(15000);
          console.log('🔔 [Mobile Push] Registered /sw.js (module)');
        } catch (modErr) {
          // Fallback to dev-sw.js
          try {
            await navigator.serviceWorker.register('/dev-sw.js?dev-sw', { scope: '/', type: 'classic' });
            registration = await waitForReady(15000);
            console.log('🔔 [Mobile Push] Registered /dev-sw.js (classic)');
          } catch (devErr) {
            // Final attempt: plain /sw.js classic registration
            await navigator.serviceWorker.register('/sw.js');
            registration = await waitForReady(15000);
            console.log('🔔 [Mobile Push] Registered /sw.js (classic)');
          }
        }
      } catch (regErr) {
        console.error('🔔 [Mobile Push] Failed to obtain service worker registration:', regErr);
        return { success: false, error: 'Service worker not active. Try refreshing or installing the app.' };
      }
    }

    // Check if service worker is actually active
    if (!registration || !registration.active) {
      console.error('🔔 [Mobile Push] Service worker registration exists but no active worker');
      return { success: false, error: 'Service worker not active yet. Try refreshing the page.' };
    }

    // Check if already subscribed; reuse existing subscription if present
    let subscription: PushSubscription | null = await registration.pushManager.getSubscription();
    if (subscription) {
      console.log('🔔 [Mobile Push] Re-using existing subscription');
    } else {
      console.log('🔔 [Mobile Push] Creating new subscription');
      // Subscribe to push manager
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
      });
      console.log('🔔 [Mobile Push] Subscription created');
    }

    // Convert subscription to JSON
    const subscriptionJSON = subscription.toJSON();
    console.log('🔔 [Mobile Push] Subscription JSON prepared');
    
    if (!subscriptionJSON.endpoint || !subscriptionJSON.keys) {
      console.error('🔔 [Mobile Push] Invalid subscription - missing endpoint or keys');
      return { success: false, error: 'Invalid subscription' };
    }

    // Save to Firestore using endpoint as unique key
    const endpointKey = subscriptionJSON.endpoint;
    const docId = `${userId}_${userEmail.replace(/[@.]/g, '_')}`; // Make email filesystem-safe
    const docRef = doc(db, 'notificationEndpoints', docId);
    
    console.log('🔔 [Mobile Push] Saving subscription to Firestore');

    await setDoc(
      docRef,
      {
        companyId,
        endpoints: {
          [endpointKey]: {
            keys: subscriptionJSON.keys,
            createdAt: serverTimestamp(),
          },
        },
      },
      { merge: true } // Merge to avoid overwriting other endpoints
    );

    console.log('✅ [Mobile Push] Subscription saved to Firestore');
    return { success: true };
  } catch (error: any) {
    console.error('❌ [Mobile Push] FAILED:', error);
    console.error('❌ [Mobile Push] Error stack:', error.stack);
    return { success: false, error: error.message || 'Subscription failed' };
  }
}

/**
 * Check if user has granted notification permission
 */
export function hasNotificationPermission(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Check current notification permission status
 */
export function getNotificationPermission(): NotificationPermission | null {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return null;
}

/**
 * Unsubscribe from push notifications on logout
 * Removes only this device's endpoint from the endpoints map, not the entire document
 */
export async function unsubscribeFromPushNotifications(
  userId?: string,
  userEmail?: string
): Promise<void> {
  console.log('🔔 [Mobile Push] Unsubscribing from push notifications', { userId, userEmail });
  
  try {
    let endpointToRemove: string | null = null;

    // Get current subscription endpoint before unsubscribing
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (subscription) {
          const subscriptionJSON = subscription.toJSON();
          endpointToRemove = subscriptionJSON.endpoint || null;
          await subscription.unsubscribe();
          console.log('✅ [Mobile Push] Unsubscribed from push manager');
        } else {
          console.log('🔔 [Mobile Push] No active subscription found');
        }
      } catch (swError) {
        console.error('❌ [Mobile Push] Service worker error:', swError);
      }
    }

    // Remove only this device's endpoint from Firestore (not the whole document)
    if (userId && userEmail && endpointToRemove && db) {
      try {
        // Use FieldPath to safely target a map key that may contain characters
        // not valid in dot-separated field paths (e.g. URLs with dots/slashes).
        const { updateDoc, doc: docFn, deleteField, getDoc, FieldPath, deleteDoc } = await import('firebase/firestore');
        const docId = `${userId}_${userEmail.replace(/[@.]/g, '_')}`;
        const docRef = docFn(db, 'notificationEndpoints', docId);

        // Remove this specific endpoint from the endpoints map using FieldPath
        await updateDoc(docRef, new FieldPath('endpoints', endpointToRemove), deleteField());

        console.log('✅ [Mobile Push] Removed endpoint from Firestore endpoints map (using FieldPath)');

        // Check if there are any endpoints left, if not, delete the document
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const endpoints = data.endpoints || {};
          if (Object.keys(endpoints).length === 0) {
            await deleteDoc(docRef);
            console.log('✅ [Mobile Push] No endpoints left, deleted document');
          }
        }
      } catch (firestoreError) {
        console.error('❌ [Mobile Push] Failed to update Firestore endpoint:', firestoreError);
      }
    } else {
      console.log('🔔 [Mobile Push] Missing userId/email/endpoint, skipping Firestore cleanup');
    }
  } catch (error) {
    console.error('❌ [Mobile Push] Failed to unsubscribe:', error);
  }
}
